import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import {
  Calendar as CalendarIcon,
  Clock,
  User,
  Mail,
  Phone,
  MessageSquare,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicBookingPage = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Data
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Form state
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    fetchAppointmentTypes();
  }, []);

  useEffect(() => {
    if (selectedDate && selectedType) {
      fetchAvailability();
    }
  }, [selectedDate, selectedType]);

  const fetchAppointmentTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/booking/types`);
      setAppointmentTypes(res.data);
    } catch (error) {
      console.error('Error fetching appointment types:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    if (!selectedDate || !selectedType) return;
    
    try {
      setLoadingSlots(true);
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await axios.get(`${API_URL}/calendar/availability?date=${dateStr}&appointment_type_id=${selectedType.id}`);
      setAvailableSlots(res.data.available_slots);
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedType || !selectedDate || !selectedTime) return;
    
    try {
      setSubmitting(true);
      
      await axios.post(`${API_URL}/booking/request`, {
        appointment_type_id: selectedType.id,
        proposed_date: format(selectedDate, 'yyyy-MM-dd'),
        proposed_time: selectedTime,
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message || null
      });
      
      setSuccess(true);
      
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Erreur lors de la réservation. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return selectedType !== null;
      case 2:
        return selectedDate !== null && selectedTime !== null;
      case 3:
        return formData.full_name && formData.email && formData.phone;
      default:
        return false;
    }
  };

  // Success screen
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
              Demande envoyée !
            </h2>
            <p className="text-gray-600 mb-6">
              Nous avons bien reçu votre demande de rendez-vous pour le{' '}
              <strong>{format(selectedDate, 'dd MMMM yyyy', { locale: fr })}</strong> à{' '}
              <strong>{selectedTime}</strong>.
            </p>
            <p className="text-sm text-gray-500 mb-8">
              Vous recevrez un email de confirmation sous 24h.
            </p>
            <Button onClick={() => navigate('/')} className="w-full">
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 py-8 px-4" data-testid="public-booking-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Button>
          <h1 className="font-heading text-3xl font-bold text-gray-900 mb-2">
            Prendre rendez-vous
          </h1>
          <p className="text-gray-600">
            Réservez votre créneau en 3 étapes simples
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                step >= s ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s}
              </div>
              {s < 3 && <div className={`w-12 h-1 mx-2 rounded transition-colors ${step > s ? 'bg-primary' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <Card className="border-0 shadow-xl overflow-hidden">
          {/* Step 1: Choose Type */}
          {step === 1 && (
            <CardContent className="p-8">
              <CardHeader className="p-0 mb-6">
                <CardTitle className="font-heading text-xl">Votre rendez-vous :</CardTitle>
              </CardHeader>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="grid gap-4">
                  {appointmentTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setSelectedType(type)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        selectedType?.id === type.id
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`appointment-type-${type.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: type.color + '20', color: type.color }}
                        >
                          <CalendarIcon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900">{type.name}</h3>
                          {type.description && (
                            <p className="text-sm text-gray-500">{type.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs font-medium bg-primary/10 text-primary px-2 py-1 rounded-full">
                          <Clock className="w-3 h-3" />
                          {type.duration_minutes || 30} min
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          )}

          {/* Step 2: Choose Date & Time */}
          {step === 2 && (
            <CardContent className="p-8">
              <CardHeader className="p-0 mb-6">
                <CardTitle className="font-heading text-xl">Date et heure</CardTitle>
                <CardDescription>
                  Choisissez le créneau qui vous convient pour votre {selectedType?.name?.toLowerCase()}
                </CardDescription>
              </CardHeader>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* Calendar */}
                <div>
                  <Label className="mb-2 block">Sélectionnez une date</Label>
                  <div className="border rounded-xl p-4">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setSelectedTime(null);
                      }}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today || date.getDay() === 0;
                      }}
                      locale={fr}
                      className="pointer-events-auto"
                    />
                  </div>
                </div>

                {/* Time Slots */}
                <div>
                  <Label className="mb-2 block">Sélectionnez une heure</Label>
                  {!selectedDate ? (
                    <div className="flex items-center justify-center h-64 bg-gray-50 rounded-xl">
                      <p className="text-gray-400">Sélectionnez d'abord une date</p>
                    </div>
                  ) : loadingSlots ? (
                    <div className="flex items-center justify-center h-64">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <div className="flex items-center justify-center h-64 bg-gray-50 rounded-xl">
                      <p className="text-gray-400 text-center">
                        Aucun créneau disponible ce jour.<br/>
                        Essayez une autre date.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => setSelectedTime(slot.time)}
                          className={`py-3 px-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                            selectedTime === slot.time
                              ? 'border-primary bg-primary text-white'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid={`time-slot-${slot.time}`}
                        >
                          {slot.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          )}

          {/* Step 3: Contact Info */}
          {step === 3 && (
            <CardContent className="p-8">
              <CardHeader className="p-0 mb-6">
                <CardTitle className="font-heading text-xl">Vos coordonnées</CardTitle>
                <CardDescription>
                  Ces informations nous permettront de vous contacter pour confirmer le rendez-vous
                </CardDescription>
              </CardHeader>
              
              {/* Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-4 text-sm">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: selectedType?.color + '20', color: selectedType?.color }}
                  >
                    <CalendarIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{selectedType?.name}</p>
                    <p className="text-gray-500">
                      {selectedDate && format(selectedDate, 'EEEE dd MMMM yyyy', { locale: fr })} à {selectedTime}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="full_name" className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Nom complet *
                  </Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Jean Dupont"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="jean.dupont@email.com"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Téléphone / WhatsApp *
                  </Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+237 6XX XXX XXX"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="message" className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Message (optionnel)
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Précisez votre demande..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </div>
            </CardContent>
          )}

          {/* Navigation */}
          <div className="bg-gray-50 px-8 py-4 flex items-center justify-between">
            {step > 1 ? (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Précédent
              </Button>
            ) : (
              <div />
            )}
            
            {step < 3 ? (
              <Button onClick={() => setStep(step + 1)} disabled={!canProceed()}>
                Suivant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={!canProceed() || submitting}
                className="bg-green-500 hover:bg-green-600"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Confirmer le rendez-vous
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PublicBookingPage;
